import type { Module, ActionContext } from 'vuex';

import type { IAppState } from '../../constants';
import {
    SET_MODAL_OPEN_STATE,
    SET_REQUIRE_BETTING_LIMIT,
    SET_TIMEOUT_STATE,
} from './responsible-gaming.constants';
import type { IResponsibleGamingState } from './responsible-gaming.constants';
import { magic } from '@gig/magic-sdk';
import {
    GamingLimitType,
    GamingLimitDuration,
} from '@gig/magic-sdk/dist/adapter/responsiblegaming/setUserLimits';
import { IUserActiveBlock } from '@gig/magic-sdk/dist/adapter/users/getUserActiveBlocks';

/**
 * Block reason id of a Time-out (self imposed cool off) block.
 */
const TIME_OUT_BLOCK_REASON_ID = 12;

export const getters = {
    requireBettingLimit(state: IResponsibleGamingState) {
        return state.requireBettingLimit;
    },
    isTimedOut(state: IResponsibleGamingState) {
        return state.isTimedOut;
    },
    timeOutResolved(state: IResponsibleGamingState) {
        return state.timeOutResolved;
    },
    isModalOpen(state: IResponsibleGamingState) {
        return state.isModalOpen;
    },
    blocks(state: IResponsibleGamingState) {
        return state.blocks;
    },
};

export const actions = {
    /**
     * Sets requireBettingLimit false if the user has the betting limits set
     */
    async setRequireBettingLimit({
        commit,
    }: ActionContext<IResponsibleGamingState, IAppState>) {
        const limits = await magic.methods.responsibleGaming.getUserLimits();

        const mandatoryLimits = limits.activeLimits.filter(
            ({ limitTypeId, durationId }) => {
                return (
                    (limitTypeId === GamingLimitType.WAGERING &&
                        durationId === GamingLimitDuration.ONE_DAY) ||
                    limitTypeId === GamingLimitType.MAX_STAKE_PER_BET
                );
            },
        );

        commit(SET_REQUIRE_BETTING_LIMIT, mandatoryLimits.length < 1);
        return mandatoryLimits.length < 1;
    },
    clearLimits({ commit }: ActionContext<IResponsibleGamingState, IAppState>) {
        commit(SET_REQUIRE_BETTING_LIMIT, null);
    },
    /**
     * Fetches the active user blocks and stores the Time-out state
     */
    async loadUserBlocks({
        commit,
    }: ActionContext<IResponsibleGamingState, IAppState>) {
        const response = await magic.methods.users.getUserActiveBlocks();

        const blocks = response || [];

        const timeOutBlocks = blocks.filter(
            (item: IUserActiveBlock) =>
                item.blockReasonId === TIME_OUT_BLOCK_REASON_ID,
        );

        const isTimedOut = timeOutBlocks.length > 0;

        commit(SET_TIMEOUT_STATE, {
            blocks:
                timeOutBlocks.sort(
                    (a: IUserActiveBlock, b: IUserActiveBlock) =>
                        new Date(b.createdDate).getTime() -
                        new Date(a.createdDate).getTime(),
                )[0] || {},
            resolved: true,
            isTimedOut,
        });

        return isTimedOut;
    },
    clearTimeoutState({
        commit,
    }: ActionContext<IResponsibleGamingState, IAppState>) {
        commit(SET_TIMEOUT_STATE, {
            resolved: false,
            isTimedOut: false,
            blocks: {},
        });
    },
    changeModalOpenState(
        { commit }: ActionContext<IResponsibleGamingState, IAppState>,
        isOpen: boolean,
    ) {
        commit(SET_MODAL_OPEN_STATE, isOpen);
    },
};

export const responsibleGaming: Module<IResponsibleGamingState, IAppState> = {
    namespaced: true,
    state: {
        requireBettingLimit: null,
        timeOutResolved: false,
        isTimedOut: false,
        isModalOpen: false,
        blocks: {},
    },
    getters,
    actions,
    mutations: {
        [SET_REQUIRE_BETTING_LIMIT](state, payload: boolean) {
            state.requireBettingLimit = payload;
        },
        [SET_TIMEOUT_STATE](
            state,
            payload: {
                resolved: boolean;
                isTimedOut: boolean;
                blocks: Partial<IUserActiveBlock>;
            },
        ) {
            state.timeOutResolved = payload.resolved;
            state.isTimedOut = payload.isTimedOut;
            state.blocks = payload.blocks;
        },
        [SET_MODAL_OPEN_STATE](state, isOpen: boolean) {
            state.isModalOpen = isOpen;
        },
    },
};
